/**
 * Helpers for translating WebAuthn / API errors into user-facing messages.
 */

// Error names raised by the browser when the user dismisses or times out the passkey prompt
const CANCELLED_ERROR_NAMES = ['NotAllowedError', 'AbortError'];

// WebAuthnError code (from @simplewebauthn/browser) when the authenticator already holds a credential
const ALREADY_REGISTERED_CODE = 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED';

/**
 * True when the user cancelled (or the browser timed out) the passkey prompt.
 */
export function isPasskeyCancelled(error: unknown): boolean {
  return error instanceof Error && CANCELLED_ERROR_NAMES.includes(error.name);
}

function isAlreadyRegistered(error: unknown): boolean {
  return error instanceof Error && (error as { code?: string }).code === ALREADY_REGISTERED_CODE;
}

const API_ERROR_MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'Passkey sign-in failed. Use a passkey registered for this admin panel.',
  ACCOUNT_DISABLED: 'This account has been disabled.',
  INVALID_REGISTRATION:
    'The registration link is invalid, expired or already used. Ask an administrator for a new one.',
  LAST_PASSKEY: 'The last remaining passkey cannot be deleted.',
  PASSKEY_EXISTS: 'This passkey is already registered.',
  SERVER_ERROR: 'Server error. Please try again later.'
};

/**
 * Convert any error thrown during a passkey flow into a message for the UI.
 */
export function describePasskeyError(error: unknown, fallback: string): string {
  if (isAlreadyRegistered(error)) {
    return 'This authenticator already has a passkey for this site.';
  }
  if (error instanceof Error) {
    return API_ERROR_MESSAGES[error.message] ?? fallback;
  }
  return fallback;
}
